import { Component, OnDestroy, OnInit, Renderer2, ViewChild, TemplateRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe, Location } from '@angular/common';

import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { flatMap, map } from 'rxjs/operators';
import { forkJoin } from 'rxjs/internal/observable/forkJoin';

import {
    Character,
    Epoch,
    ExExercise,
    Lesson,
    LessonPrivilegeSettingsForForm,
    Privilege,
    School,
    SchoolSubject,
    StudentClass,
    Currency,
    Profile
} from '@df/interfaces';
import { TeacherService } from '@app/teacher/services/teacher.service';
import { PrivilegeService } from '@app/core/services/privilege.service';
import { ExExerciseService } from '@app/core/services/ex_exercise.service';
import { TaskService } from '@app/core/services/task.service';

import { ENTER_KEY_CODE, LESSON_CANCELED, LESSON_CREATED, DEFAULT_ACCESS_RIGHTS } from '@app/app.constants';

import { declensionOfNumerals } from '@app/_utils/helpers';

import { armIcon } from '@app/app.constants';
import { checkboxUncheckedIcon } from '@app/app.constants';
import { checkboxCheckedIcon } from '@app/app.constants';
import { MatDialog } from '@angular/material/dialog';
import { PeopleService } from '@app/core/services';
import { AccountService } from '@app/teacher/services/account.service';

@Component({
    selector: 'app-create-new-lesson',
    templateUrl: './create-new-lesson.component.html',
    styleUrls: ['./create-new-lesson.component.scss']
})
export class CreateNewLessonComponent implements OnInit, OnDestroy {
    @ViewChild('nonClassesMsg', { static: true }) nonClassesMsg: TemplateRef<any>;

    defaultEpoch = 0;
    defaultEpochID = 1;
    defaultSubject = 0;

    lesson: Lesson;
    schools: Array<School>;
    classes: Array<StudentClass>;
    epochs: Array<Epoch>;
    subjects: Array<SchoolSubject>;

    schoolAndClass: Array<{
        school_id: number;
        school_title: string;
        class_id: number;
        class_title: string;
    }> = [];

    selectedSchoolAndClass: {
        school_id: number;
        class_id: number;
    };

    createNewLessonForm: FormGroup;
    exercises: Array<number> = [];
    isEditRoute = false;
    availableLessonNumbers: Array<number> = [];
    lessonNumber: number;
    rewardWithCash = false;
    paymentForTaskWithCash = true;
    hasKairosBonus = false;
    exExercises: ExExercise[] = [];
    exExercise: ExExercise;
    rewardRegexpStr = /^\d+(\.?\d?\d?)?$/;
    headerName = '';
    isLoaded = false;
    isAccessSave = false;
    isPrivilegesOpen = false;
    isExExercisesOpen = false;
    isSmartSprintOpen = false;
    privileges: Privilege[] = [];
    characters: Character[] = [];
    selectedCharacters: Character[] = [null, null, null, null];
    smartSprints = [];
    currentCurrency: Currency;
    declensionOfNumerals = declensionOfNumerals;
    isHabirint: boolean = false;
    dialogRef: any;
    lessonForOnline: boolean = false;
    profile: Profile;
    groupRights = DEFAULT_ACCESS_RIGHTS;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    armIcon = armIcon;
    checkboxCheckedIcon = checkboxCheckedIcon;
    checkboxUncheckedIcon = checkboxUncheckedIcon;

    constructor(
        private teacherService: TeacherService,
        private peopleService: PeopleService,
        private accountService: AccountService,
        private privilegeService: PrivilegeService,
        private exExerciseService: ExExerciseService,
        private tasksService: TaskService,
        private activeRoute: ActivatedRoute,
        private datePipe: DatePipe,
        private fb: FormBuilder,
        private router: Router,
        public dialog: MatDialog,
        private location: Location,
        private renderer: Renderer2
    ) {}

    ngOnInit(): void {
        this.accountService.getProfileData().subscribe(response => {
            if (response.data && response.data.user.username) {
                this.profile = response.data;
                this.checkAccess();
            }
        });

        this.initComponent();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    openDialog(): void {
        this.dialogRef = this.dialog.open(this.nonClassesMsg, {
            width: '400px',
            height: '240px'
        });
    }

    closeDialog(): void {
        this.dialogRef.close();
        this.router.navigate(['/teacher/prepare']);
    }

    private setEditLessonAccessSave(lesson: Lesson) {
        // разрешаем если статус урока: Не проведенно, Готово
        if (lesson.status === LESSON_CANCELED || lesson.status === LESSON_CREATED) {
            this.isAccessSave = true;
        }

        // если пользователь является автором урока то разрешаем
        if (lesson.is_author) {
            this.isAccessSave = true;
            return;
        }

        // проверяем прова доступа на редактирование чужого урока
        if (this.groupRights.edit_other_lesson.is_access) {
            this.isAccessSave = true;

            // проверяем права доступа к редактированию шаблонов
            if (lesson.student_class_obj.title.includes('Шаблон')) {
                this.isAccessSave = this.groupRights.edit_templates.is_access;
                return;
            }
            return;
        }
        this.isAccessSave = false;
    }

    makeDataForEditLessonPage(): void {
        const lesson_id: string = this.activeRoute.snapshot.firstChild.params['lesson_id'];

        this.getLessonExExercise(Number(lesson_id));

        this.teacherService
            .getLessonById(lesson_id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((lesson: Lesson) => {
                this.handleLessonData(lesson);
                this.isLoaded = true;
                this.setEditLessonAccessSave(lesson);
                this.isHabirint = lesson.is_habirint;
            });
    }

    makeDataForCreateLessonPage(): void {
        const date = new Date();
        const correct_date = this.datePipe.transform(date, 'yyyy-MM-ddTHH:mm');
        const defaultSubject = this.subjects[this.defaultSubject]['id'];
        const defaultEpoch = this.epochs[this.defaultEpoch]['id'];

        this.isAccessSave = true;

        this.createNewLessonForm.controls['date'].setValue(correct_date);
        this.createNewLessonForm.controls['subjects'].setValue(defaultSubject);
        this.createNewLessonForm.controls['epochs'].setValue(defaultEpoch);

        /*
         * Отправляю http-запрос для получения класов текущего учителя.
         */
        this.peopleService.getCurrentUserClasses().subscribe(response => {
            this.classes = response.data;
            /*
             * Перебираю классы и школы и смотрю чтобы определённый класс пренадлежал
             * определённой школе, затем формирую объект школа/класс
             */
            this.classes.forEach(studentClass => {
                this.schools.forEach(school => {
                    if (school.id === studentClass.school.id) {
                        this.schoolAndClass.push({
                            school_id: school['id'],
                            school_title: school['title'],
                            class_id: studentClass['id'],
                            class_title: studentClass['title']
                        });
                    }
                });
            });
            /*
             * Ложу дефолтное значение в select 'Школа | Класс'.
             */
            const defaultClass = this.schoolAndClass[0];

            this.createNewLessonForm.controls['teacherClasses'].setValue(defaultClass['class_id']);

            /*
             * Кладу текущий класс/школа в отдельный объект, чтобы затем при submit
             * не пришлось делать дополнительных циклов с проверками.
             */
            this.selectedSchoolAndClass = {
                school_id: defaultClass['school_id'],
                class_id: defaultClass['class_id']
            };

            /*
             * Формирую список htpp-запросов для получения последних номеров уроков у всех
             * классов текущего учителя
             */
            const classId = defaultClass['class_id'];
            this.getAvailableLessonNumbers(classId);
            this.isLoaded = true;
            this.getAllExExercises(classId);
        });

        this.getEpochCharacter(this.defaultEpochID);
    }

    onSubmit(): void {
        /*
         * При клике на кнопку 'ОК', производит сборку необходимых данных на сервер
         */
        this.applyExtraExercise();
        const lessonDataFromForm = {
            title: this.createNewLessonForm.get('lessonName').value,
            is_online: this.lessonForOnline,
            remarks: this.createNewLessonForm.get('lessonDescription').value,
            date: new Date(this.createNewLessonForm.get('date').value),
            reward_with_cash: this.rewardWithCash,
            payment_for_task_with_cash: this.paymentForTaskWithCash,
            has_kairos_bonus: this.hasKairosBonus,
            subject: +this.createNewLessonForm.get('subjects').value,
            epoch: +this.createNewLessonForm.get('epochs').value,
            lesson_number: +this.createNewLessonForm.get('lessonNumber').value,
            student_class: +this.selectedSchoolAndClass['class_id'],
            privileges: this.createNewLessonForm.get('privileges').value,
            ex_exercise: this.exExercise,
            smart_sprints: this.createNewLessonForm.get('smartSprint').value,
            is_habirint: this.isHabirint
        };

        if (this.isEditRoute) {
            this.editLesson(lessonDataFromForm);
        } else {
            this.createLesson(lessonDataFromForm);
        }
    }

    createLesson(lessonData: any) {
        this.teacherService
            .createNewLesson(lessonData.student_class, lessonData)
            .pipe(takeUntil(this.destroy$))
            .subscribe((next: Lesson) => {
                if (next && next.student_class && next.subject) {
                    this.navigateToLessonEdit(next.id);
                }
            });
    }

    editLesson(editedLessonData) {
        this.teacherService
            .editLesson(this.lesson.id, editedLessonData)
            .pipe(takeUntil(this.destroy$))
            .subscribe(updatedLesson => {
                if (updatedLesson) {
                    this.navigateToLessonEdit(updatedLesson.id);
                }
            });
    }

    navigateToLessonEdit(lessonID: number) {
        this.router.navigate(['/teacher', 'prepare', lessonID, 'edit']);
    }

    onCancel(): void {
        this.location.back();
    }

    changeClass(): void {
        /*
         * Даная функция вызывается когда пользователь меняет в select класс
         */
        const selectedClassId = this.createNewLessonForm.get('teacherClasses').value;

        /*
         * Перебираю существующие классы чтобы найти тот класс который выбрал пользователь,
         * затем сохраняю выбранные данные школы и класса, чтобы затем можно было их
         * использовать когда пользователь нажмёт submit
         */
        this.schoolAndClass.forEach(currentClass => {
            if (currentClass['class_id'] === Number(selectedClassId)) {
                this.selectedSchoolAndClass['class_id'] = currentClass['class_id'];
                this.selectedSchoolAndClass['school_id'] = currentClass['school_id'];
            }
        });

        const classId = this.selectedSchoolAndClass['class_id'];
        this.getAvailableLessonNumbers(classId);
        this.getAllExExercises(classId);
    }

    createDataClassAndSchool(schoolTitle, classTitle): string {
        return `Шк. ${schoolTitle} | ${classTitle}`;
    }

    getSchools(): any {
        /*
         * Вызывает сервис с http-запросом для получения всех школ
         */
        return this.privilegeService.getSchools().pipe(
            flatMap((next: any) => {
                this.schools = next.results;
                return next.results;
            })
        );
    }

    getAvailableLessonNumbers(classId: number): any {
        return this.teacherService.getAvailableLessonNumbers(classId).subscribe(response => {
            this.availableLessonNumbers = response['available_numbers'];
            this.createLessonNumberSelect();
        });
    }

    getEpochs(): any {
        /*
         * Вызывает сервис с http-запросом для получения всех эпох
         */
        return this.privilegeService.getEpochs().pipe(
            flatMap((next: any) => {
                this.epochs = next.results;
                return next.results;
            })
        );
    }

    getSubjects(): any {
        /*
         * Вызывает сервис с http-запросом для получения всех предметов
         * (математика, литература и т.д)
         */
        return this.teacherService.getTeacherSubjects().pipe(
            flatMap((next: any) => {
                this.subjects = next.results;
                if (!next.results.length) {
                    this.openDialog();
                }
                return next.results;
            })
        );
    }

    createLessonNumberSelect(): void {
        /*
         * Функция берёт существующий список номеров урока, чистит его,
         * добавляет в него указанный номер по умалчанию и добавляет в него ещё
         * два номера. Сделанно это для того, чтобы дать возможность пользователю
         * выбирать из нескольких значений.
         */
        let lessonNumber = this.lessonNumber;
        if (!lessonNumber) {
            lessonNumber = this.availableLessonNumbers[0];
        }

        if (this.isEditRoute) {
            this.availableLessonNumbers.splice(0, 0, lessonNumber);
        }

        this.createNewLessonForm.get('lessonNumber').patchValue(lessonNumber);
    }

    getPrivilegeControls(): Array<AbstractControl> {
        return (<FormArray>this.createNewLessonForm.get('privileges')).controls;
    }

    getSmartSprintControls(): Array<AbstractControl> {
        const controls = (<FormArray>this.createNewLessonForm.get('smartSprint')).controls;
        return controls;
    }

    onAddPrivilege(): void {
        (<FormArray>this.createNewLessonForm.get('privileges')).push(
            new FormGroup({
                character: new FormControl(null, [Validators.required, Validators.pattern(/^[1-9]+[0-9]*$/)]),
                privilege: new FormControl(null, [Validators.required, Validators.pattern(/^[1-9]+[0-9]*$/)]),
                applyToAll: new FormControl(false)
            })
        );
    }

    onAddSmartSprint(): void {
        (<FormArray>this.createNewLessonForm.get('smartSprint')).push(
            new FormGroup({
                id: new FormControl(0, []),
                lesson: new FormControl(0, []),
                box: new FormControl(0, []),
                duration: new FormControl(0, [Validators.required]),
                reward: new FormControl(0, [Validators.required])
            })
        );
    }

    getCharacter(characterID): Character {
        if (characterID) {
            const index = this.characters.findIndex(c => c.id === +characterID);
            return this.characters[index];
        }
        return null;
    }

    canAddPrivilege(): boolean {
        return (<FormArray>this.createNewLessonForm.get('privileges')).length < 4;
    }

    onRemovePrivilege(index: number): void {
        (<FormArray>this.createNewLessonForm.get('privileges')).removeAt(index);
        this.selectedCharacters[index] = null;
    }

    onRemoveSmartSprint(index: number): void {
        (<FormArray>this.createNewLessonForm.get('smartSprint')).removeAt(index);
    }

    activateCharacter(privilegeControlIndex, control: AbstractControl): void {
        const selectedCharacterID = +control.value.character;
        const index = this.characters.findIndex(character => character.id === selectedCharacterID);
        this.selectedCharacters[privilegeControlIndex] = this.characters[index];
        this.checkForDuplicateCharacter();
    }

    changeExExercise() {
        const exObject = this.createNewLessonForm.get('exExerciseObject');
        const value = exObject.value.exExerciseTile;
        this.exExercise = this.exExercises.filter(ex => ex.id === Number(value))[0];
        if (this.exExercise) {
            const exExerciseDescription = this.exExercise.description;
            const exExerciseReward = this.exExercise.reward;
            exObject.get('exExerciseDescription').setValue(exExerciseDescription);
            exObject.get('exExerciseReward').setValue(exExerciseReward);
        }
    }

    textAreaAdjust($event: any) {
        const descriptionTextArea = $event.target;
        this.renderer.setStyle(descriptionTextArea, 'height', `${descriptionTextArea.scrollHeight}px`);
        this.exExercise.description = this.createNewLessonForm
            .get('exExerciseObject')
            .get('exExerciseDescription').value;
        if ($event.keyCode === ENTER_KEY_CODE) {
            descriptionTextArea.value += '\n';
        }
    }

    applyExtraExercise() {
        const extraExerciseFG = this.createNewLessonForm.get('exExerciseObject');
        const description = extraExerciseFG.get('exExerciseDescription').value;
        const reward = extraExerciseFG.get('exExerciseReward').value;
        if (this.exExercise) {
            this.exExercise.description = description;
            this.exExercise.reward = +reward;
        }
    }

    private initComponent(): void {
        this.createNewLessonForm = this.fb.group({
            lessonName: new FormControl('', Validators.required),
            lessonDescription: new FormControl(''),
            date: new FormControl('', Validators.required),
            epochs: new FormControl('', Validators.required),
            subjects: new FormControl('', Validators.required),
            teacherClasses: new FormControl('', Validators.required),
            lessonNumber: new FormControl('', Validators.required, this.checkForLessonNumber.bind(this)),
            privileges: new FormArray([]),
            smartSprint: new FormArray([]),
            exExerciseObject: this.fb.group({
                exExerciseTile: new FormControl(''),
                exExerciseDescription: new FormControl(''),
                exExerciseReward: new FormControl(
                    '',
                    Validators.compose([Validators.pattern(this.rewardRegexpStr), Validators.min(0)])
                )
            })
        });

        forkJoin(this.getSubjects(), this.getEpochs(), this.getSchools()).subscribe(() => {
            if (this.activeRoute.snapshot.firstChild) {
                this.makeDataForEditLessonPage();
            } else {
                this.makeDataForCreateLessonPage();
            }
        });
        this.getPrivileges();
    }

    private getEpochCharacter(epochId): void {
        console.log(epochId);
        this.privilegeService.getEpochCharacters(epochId).subscribe(resp => {
            if (!resp.error) {
                this.characters = resp.data;
            }
        });
    }

    private getPrivileges(): void {
        this.privilegeService.getPrivileges().subscribe(resp => {
            this.privileges = resp.results;
        });
    }

    private checkForDuplicateCharacter(): void {
        const privilegesFormArray = this.createNewLessonForm.get('privileges') as FormArray;
        for (let i = 0; i < privilegesFormArray.length; i++) {
            const characterFormControl = privilegesFormArray.at(i).get('character');
            const character = this.getCharacter(+characterFormControl.value);
            for (let j = 0; j < this.selectedCharacters.length; j++) {
                if (i !== j && this.selectedCharacters[j]) {
                    if (character.id === this.selectedCharacters[j].id) {
                        characterFormControl.setErrors({ forbiddenCharacter: true });
                        break;
                    }
                    characterFormControl.setErrors(null);
                }
            }
        }
    }

    private handleLessonData(next: Lesson): void {
        this.lesson = next;
        const lessonNumber = this.lesson['lesson_number'];
        const date = this.teacherService.convertDateFromServerTimeToLocal(this.lesson);
        const lessonName = this.lesson['title'];
        const lessonDescription = this.lesson['remarks'];
        this.isEditRoute = true;
        this.lessonNumber = lessonNumber;
        this.smartSprints = this.lesson.smart_sprints;
        this.createNewLessonForm.controls['date'].setValue(date);
        this.createNewLessonForm.controls['lessonNumber'].setValue(lessonNumber);
        this.createNewLessonForm.controls['epochs'].setValue(this.lesson['epoch']);
        this.createNewLessonForm.controls['subjects'].setValue(this.lesson['subject']);
        this.rewardWithCash = this.lesson.reward_with_cash;
        this.paymentForTaskWithCash = this.lesson.payment_for_task_with_cash;
        this.hasKairosBonus = this.lesson.has_kairos_bonus;
        this.lessonForOnline = this.lesson.is_online;

        this.changeEpoch(this.lesson['epoch']);
        this.fillPrivilegesFormArray(this.lesson.privileges);
        this.fillSmartSprintsFormArray();
        /*
         * Перебираю все школы, чтобы найти ту школу которая пренадлежит текущему
         * уроку, затем формирую данные для дальнейшего переиспользования.
         */
        this.schools.forEach(school => {
            if (school.id === this.lesson.student_class_obj.school.id) {
                this.schoolAndClass.push({
                    school_title: school.title,
                    school_id: this.lesson.student_class_obj.school.id,
                    class_id: this.lesson.student_class,
                    class_title: this.lesson.student_class_obj.title
                });
                this.selectedSchoolAndClass = {
                    school_id: this.schoolAndClass[0].school_id,
                    class_id: this.schoolAndClass[0].class_id
                };
            }
        });
        /*
         * Устанавливаю дефолтное значение в select выбора школа/класс.
         */
        const classId = this.schoolAndClass[0].class_id;
        this.createNewLessonForm.controls['teacherClasses'].setValue(classId);

        if (lessonName) {
            this.createNewLessonForm.controls['lessonName'].setValue(lessonName);
        }
        if (lessonDescription) {
            this.createNewLessonForm.controls['lessonDescription'].setValue(lessonDescription);
        }
        this.getAvailableLessonNumbers(classId);
        this.getAllExExercises(this.selectedSchoolAndClass['class_id']);
    }

    private fillPrivilegesFormArray(privileges: LessonPrivilegeSettingsForForm[]): void {
        if (privileges.length > 0) {
            this.isPrivilegesOpen = true;
            const control = this.createNewLessonForm.get('privileges') as FormArray;
            for (let i = 0; i < privileges.length; i++) {
                const privilege = privileges[i];
                control.push(
                    new FormGroup({
                        character: new FormControl(privilege.character, [
                            Validators.required,
                            Validators.pattern(/^[1-9]+[0-9]*$/)
                        ]),
                        privilege: new FormControl(privilege.privilege, [
                            Validators.required,
                            Validators.pattern(/^[1-9]+[0-9]*$/)
                        ]),
                        applyToAll: new FormControl(false)
                    })
                );
                this.selectedCharacters[i] = this.getCharacter(privilege.character);
            }
        }
    }

    private fillSmartSprintsFormArray() {
        // Заполняет элементы смарт спринта
        if (this.smartSprints) {
            this.smartSprints.forEach(item => {
                (<FormArray>this.createNewLessonForm.get('smartSprint')).push(
                    new FormGroup({
                        id: new FormControl(item.id, []),
                        lesson: new FormControl(item.lesson, []),
                        box: new FormControl(item.box, []),
                        duration: new FormControl(item.duration, [Validators.required]),
                        reward: new FormControl(item.reward, [Validators.required])
                    })
                );
            });
            this.isSmartSprintOpen = true;
        }
    }

    private checkForLessonNumber(control: AbstractControl) {
        /**
         * Метод валидатор проверяет выбранный номер урока на занятость
         * если номер занят, то его использовать нельзя.
         */
        if (this.isLoaded) {
            const classId = +this.selectedSchoolAndClass['class_id'];
            const lessonNumber = +control.value;
            return this.teacherService.checkLessonNumber(classId, lessonNumber).pipe(
                map(response => {
                    const isLessonExist = response['is_lesson_exists'];
                    if (isLessonExist && !this.isEditRoute) {
                        return { lessonNumberExist: true };
                    } else if (isLessonExist && this.lessonNumber !== lessonNumber) {
                        return { lessonNumberExist: true };
                    } else {
                        return null;
                    }
                })
            );
        }
        // Ничего не возвращаем, так как данные еще не подгрузились
        return Promise.resolve(null);
    }

    private getLessonExExercise(lessonId: number) {
        this.exExerciseService
            .getLessonExExercise(lessonId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((extraExercise: ExExercise) => {
                this.exExercise = extraExercise;
                const exObj = this.createNewLessonForm.controls['exExerciseObject'];
                if (extraExercise.id !== 0) {
                    exObj.get('exExerciseTile').setValue(extraExercise.id, { onlySelf: true });
                    exObj.get('exExerciseReward').setValue(extraExercise.reward);
                    exObj.get('exExerciseDescription').setValue(extraExercise.description);
                    this.isExExercisesOpen = true;
                } else {
                    exObj.get('exExerciseTile').setValue(null, { onlySelf: true });
                }
            });
    }

    private getAllExExercises(classId: number) {
        this.exExerciseService
            .getAllExExercises(classId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(resp => {
                this.exExercises = resp;
            });
    }

    private getEpochCurrency(epochId): void {
        this.tasksService.getEpochCurrency(epochId).subscribe(resp => {
            if (resp.results) {
                this.currentCurrency = resp.results[0];
            }
        });
    }

    changeEpoch(eventEpochId) {
        const epochId = Number(eventEpochId);

        this.getEpochCurrency(epochId);
        this.getEpochCharacter(epochId);
    }

    handleLessonOnline() {
        this.lessonForOnline = !this.lessonForOnline;
    }

    private checkAccess(): void {
        const user_rights = this.profile.group.rights;

        user_rights.forEach(item => {
            switch (+item.index_title) {
                // доступ к изменению статуса урока
                case this.groupRights.change_status_other_lesson.index_title:
                    this.groupRights.change_status_other_lesson.is_access = true;
                    break;
                // доступ к копированию чужих уроков
                case this.groupRights.copy_other_lesson.index_title:
                    this.groupRights.copy_other_lesson.is_access = true;
                    break;
                // доступ к созданию урока
                case this.groupRights.create_lesson.index_title:
                    this.groupRights.create_lesson.is_access = true;
                    break;
                // доступ к удалению чужого урока
                case this.groupRights.delete_other_lesson.index_title:
                    this.groupRights.delete_other_lesson.is_access = true;
                    break;
                // доступ к редактированию чужого урока
                case this.groupRights.edit_other_lesson.index_title:
                    this.groupRights.edit_other_lesson.is_access = true;
                    break;
                // доступ к редактированию шаблонов
                case this.groupRights.edit_templates.index_title:
                    this.groupRights.edit_templates.is_access = true;
                    break;
                // доступ к копированию шаблонов
                case this.groupRights.copy_templates.index_title:
                    this.groupRights.copy_templates.is_access = true;
                    break;
                // доступ к удалению шаблонов
                case this.groupRights.delete_templates.index_title:
                    this.groupRights.delete_templates.is_access = true;
                    break;
                default:
                    break;
            }
        });
    }
}
